import { safeGetJson, safeRemoveItem, safeSetJson } from "./safeStorage";

export const getPreviewHttpJsonConfig = (timeout = 30000) => ({
    headers: { "Content-Type": "application/json" },
    timeout,
});

export const stablePreviewStringify = (value) => {
    if (typeof value === "undefined") return "undefined";
    if (value === null || typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stablePreviewStringify).join(",")}]`;

    return `{${Object.keys(value).sort().map((key) => (
        `${JSON.stringify(key)}:${stablePreviewStringify(value[key])}`
    )).join(",")}}`;
};

export const hashPreviewString = (value) => {
    let hash = 0;
    for (let index = 0; index < value.length; index++) {
        hash = ((hash << 5) - hash) + value.charCodeAt(index);
        hash |= 0;
    }
    return String(hash >>> 0);
};

export const sanitizePreviewStorageKeyPart = (value) => String(value || "none")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .slice(0, 80);

export const normalizePreviewPollInterval = (value, fallback = 3000) => {
    const numericValue = Number(value);
    const numericFallback = Number(fallback);
    if (!Number.isFinite(numericValue) || numericValue < 500) {
        return Number.isFinite(numericFallback) && numericFallback >= 500
            ? numericFallback
            : 3000;
    }
    return numericValue;
};

export const getPreviewPollStatus = (payload) => String(payload?.status || "").toLowerCase();
export const isPreviewPollCompleted = (payload) => (
    ["completed", "complete", "success", "succeeded", "done"]
        .includes(getPreviewPollStatus(payload))
);
export const isPreviewPollFailed = (payload) => (
    ["failed", "error", "cancelled", "canceled"].includes(getPreviewPollStatus(payload))
);
export const isPreviewPollRunning = (payload) => (
    ["", "queued", "pending", "processing", "running", "in_progress", "started"]
        .includes(getPreviewPollStatus(payload))
);
export const extractPreviewPollJobId = (payload) => (
    payload?.jobId || payload?.jobID || payload?.id || null
);
export const extractPreviewPollResult = (payload, acceptsPayload = () => false) => {
    if (!payload || typeof payload !== "object") return null;
    if (Object.prototype.hasOwnProperty.call(payload, "result")) return payload.result;
    if (Object.prototype.hasOwnProperty.call(payload, "data")) return payload.data;
    if (Object.prototype.hasOwnProperty.call(payload, "payload")) return payload.payload;
    if (Object.prototype.hasOwnProperty.call(payload, "response")) return payload.response;
    return acceptsPayload(payload) ? payload : null;
};

export const createPreviewCancellation = ({ cancelCode, isActive }) => {
    let timeoutHandle = null;

    const clear = () => {
        if (timeoutHandle) {
            clearTimeout(timeoutHandle);
            timeoutHandle = null;
        }
    };
    const createError = () => {
        const error = new Error(cancelCode);
        error.code = cancelCode;
        return error;
    };
    const isCancelled = (error) => error?.code === cancelCode || error?.message === cancelCode;
    const assertActive = () => {
        if (!isActive()) throw createError();
    };
    const wait = (intervalMs) => new Promise((resolve, reject) => {
        if (!isActive()) {
            reject(createError());
            return;
        }
        clear();
        timeoutHandle = setTimeout(() => {
            timeoutHandle = null;
            if (!isActive()) {
                reject(createError());
                return;
            }
            resolve();
        }, intervalMs);
    });

    return { clear, createError, isCancelled, assertActive, wait };
};

export const createPreviewPollStorage = ({
    prefix,
    getBookId,
    getProductType = () => null,
    includeProductTypeInKey = false,
    getPollStartUrl,
    getPollStatusUrl,
    getPollTimeoutMs,
}) => {
    const createRequestSignature = (requestBody) => hashPreviewString(stablePreviewStringify({
        step1Values: requestBody.step1Values,
        step2Values: requestBody.step2Values,
        variantID: requestBody.variantID,
        ...(includeProductTypeInKey ? { productType: requestBody.productType } : {}),
        pollStartUrl: getPollStartUrl(),
        pollStatusUrl: getPollStatusUrl(),
    }));
    const getStorageKey = (requestBody) => [
        prefix,
        sanitizePreviewStorageKeyPart(getBookId() || "no-book"),
        ...(includeProductTypeInKey
            ? [sanitizePreviewStorageKeyPart(requestBody.productType)]
            : []),
        sanitizePreviewStorageKeyPart(requestBody.variantID || "no-variant"),
        createRequestSignature(requestBody),
    ].join(":");
    const isExpired = (pollJob) => {
        const expiresAtMs = Date.parse(pollJob?.expiresAt || "");
        if (Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now()) return true;
        const timeoutMs = Number(getPollTimeoutMs());
        const createdAt = Number(pollJob?.createdAt);
        return Number.isFinite(timeoutMs) && timeoutMs > 0 &&
            Number.isFinite(createdAt) && Date.now() - createdAt >= timeoutMs;
    };
    const clear = (storageKey) => safeRemoveItem(storageKey);
    const get = (requestBody) => {
        const storageKey = getStorageKey(requestBody);
        const storedJob = safeGetJson(storageKey, null);
        const requestSignature = createRequestSignature(requestBody);
        const canReuse = storedJob?.jobId &&
            storedJob.requestSignature === requestSignature &&
            storedJob.pollStartUrl === getPollStartUrl() &&
            storedJob.pollStatusUrl === getPollStatusUrl() &&
            !isExpired(storedJob);
        if (!canReuse) {
            if (storedJob) clear(storageKey);
            return null;
        }
        return { ...storedJob, storageKey };
    };
    const persist = (storageKey, requestBody, pollJob) => {
        const existingJob = safeGetJson(storageKey, {});
        const updatedJob = {
            ...existingJob,
            ...pollJob,
            storageKey,
            requestSignature: createRequestSignature(requestBody),
            pollStartUrl: getPollStartUrl(),
            pollStatusUrl: getPollStatusUrl(),
            ...(getProductType() ? { productType: requestBody.productType } : {}),
            variantID: requestBody.variantID,
            bookId: getBookId() || null,
            createdAt: existingJob.createdAt || pollJob.createdAt || Date.now(),
            updatedAt: Date.now(),
        };
        safeSetJson(storageKey, updatedJob);
        return updatedJob;
    };

    return {
        createRequestSignature,
        getStorageKey,
        isExpired,
        get,
        persist,
        clear,
        clearForRequest: (requestBody) => clear(getStorageKey(requestBody)),
    };
};

export const restorePreviewDraftValues = (draftData, pageSettings) => {
    if (!draftData || !pageSettings) return;
    ["step1", "step2", "step3"].forEach((step) => {
        const values = draftData[`${step}Values`];
        const storageKey = pageSettings[`${step}StorageKey`];
        if (values && storageKey) safeSetJson(storageKey, values);
    });
};

export const hasPreviewStoredValues = (storageKey) => {
    if (!storageKey) return false;
    const parsed = safeGetJson(storageKey, {});
    return parsed && typeof parsed === "object" && Object.keys(parsed).length > 0;
};

// Mirrors the n8n preview contract (Preview_General "Require Uploaded Image for
// Story Preview"): every generated preview except songs and the Love Story Map
// needs the customer photo from step 1 in step1Values.image.value.
const PREVIEW_TYPES_WITHOUT_STEP1_IMAGE = ["song", "love-story-map"];

export const isMissingRequiredStep1Image = (productType, step1Values) => (
    Boolean(productType) &&
    !PREVIEW_TYPES_WITHOUT_STEP1_IMAGE.includes(productType) &&
    !String(step1Values?.image?.value || "").trim()
);

// Restores the upload's required placeholder so the existing step-1 and
// checkout validation block again until the photo is uploaded.
export const markStep1ImageRequired = (storageKey, step1Values) => {
    if (!storageKey) return;
    safeSetJson(storageKey, {
        ...step1Values,
        image: { ...step1Values?.image, value: "", required: true },
    });
};

// Remembers where the last successful photo upload was stored, so a later
// missing_required_image can tell "never uploaded" from "uploaded, then lost".
const IMAGE_UPLOAD_MARKER_KEY = "customizerImageUpload";
const IMAGE_UPLOAD_MARKER_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export const recordImageUpload = (storageKey) => {
    safeSetJson(IMAGE_UPLOAD_MARKER_KEY, { storageKey: storageKey || "none", at: Date.now() });
};

// What the stored step-1 photo entry looked like when the preview found no photo:
// "missing" (no entry), "required" or "optional" (the stored required flag).
export const getStep1ImageEntryState = (step1Values) => {
    const entry = step1Values?.image;
    if (!entry || typeof entry !== "object") return "missing";
    return entry.required === true ? "required" : "optional";
};

export const getImageUploadState = (step1StorageKey) => {
    const marker = safeGetJson(IMAGE_UPLOAD_MARKER_KEY, null);
    if (!marker?.storageKey || Date.now() - Number(marker.at) > IMAGE_UPLOAD_MARKER_MAX_AGE_MS) {
        return "none";
    }
    return marker.storageKey === step1StorageKey ? "same_key" : "other_key";
};

export const runPreviewRequest = async ({
    requestMode,
    requestBody,
    minLoadingDuration,
    setLoadingPercentage,
    startPolling,
    fetchPreview,
    applyFetchResult,
}) => {
    const minLoadingPromise = new Promise((resolve) => {
        let percentage = 0;
        const intervalMs = Math.max(0, (minLoadingDuration * 1000) / 99);
        const loadingInterval = setInterval(() => {
            percentage = Math.min(99, percentage + 1);
            setLoadingPercentage(percentage);
            if (percentage >= 99) {
                clearInterval(loadingInterval);
                resolve();
            }
        }, intervalMs);
    });
    const requestPromise = (requestMode === "poll"
        ? startPolling(requestBody)
        : fetchPreview(requestBody).then(applyFetchResult))
        .then((value) => ({ ok: true, value }))
        // sentry-ignore: error is rethrown right below after the minimum loading time
        .catch((error) => ({ ok: false, error }));

    const [, outcome] = await Promise.all([minLoadingPromise, requestPromise]);
    if (!outcome.ok) throw outcome.error;
    return outcome.value;
};
